import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Outlet,
  Navigate,
} from "react-router-dom";

// Login loads immediately
import Login from "./pages/Login";

// Other pages load only when their routes are visited
const Drawer = lazy(() => import("./components/Drawer"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const UpdateProfile = lazy(() => import("./pages/UpdateProfile"));
const MyReferrals = lazy(() => import("./components/MyReferrals"));
const GlobalReferrals = lazy(() => import("./components/GlobalReferrals"));
const CompanyUsers = lazy(() => import("./components/CompanyUsers"));
const AddCompanyUsers = lazy(() => import("./components/AddCompanyUsers"));
const CompanyLogin = lazy(() => import("./pages/CompanyLogin"));
const CompanyDashboard = lazy(() => import("./pages/CompanyDashboard"));
const AddReferral = lazy(() => import("./components/AddReferral"));
const AddBulkReferral = lazy(() => import("./components/AddBulkReferral"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const CompanyMaster = lazy(() => import("./components/CompanyMaster"));
const AddCompany = lazy(() => import("./components/AddCompany"));
const GiveCategory = lazy(() => import("./components/GiveCategory"));
const AddCategory = lazy(() => import("./components/AddCategory"));
const Subjects = lazy(() => import("./components/Subjects"));
const TopContributors = lazy(() => import("./pages/TopContributors"));
const CompanyQuizReport = lazy(() => import("./components/CompanyQuizReport"));
const CompanyKbProgress = lazy(() => import("./pages/CompanyKbProgress"));
const Groups = lazy(() => import("./pages/Groups"));
const AddMentor = lazy(() => import("./pages/AddMentor"));
const AddMentees = lazy(() => import("./pages/AddMentees"));
const ViewMentees = lazy(() => import("./pages/ViewMentees"));
const Attempts = lazy(() => import("./pages/Attempts"));
const ConnectionRequest = lazy(() => import("./pages/ConnectionRequest"));
const ViewMenteesbyMentor = lazy(() => import("./pages/ViewMenteesbyMentor"));
const KnowledgeBased = lazy(() => import("./pages/KnowledgeBased"));
const Chapters = lazy(() => import("./components/Chapters"));
const Topics = lazy(() => import("./components/Topics"));
const Sections = lazy(() => import("./components/Sections"));

const Loading = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-100">
    <p className="text-gray-600 text-sm">Loading...</p>
  </div>
);

const App = () => {
  return (
    <Router>
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* User Login */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* User Dashboard Routes */}
          <Route
            element={
              <Drawer>
                <Outlet />
              </Drawer>
            }
          >
            <Route path="/user-dashboard" element={<Dashboard />} />
            <Route path="/top-contributors" element={<TopContributors />} />
            <Route path="/update-profile" element={<UpdateProfile />} />
            <Route path="/my-referrals" element={<MyReferrals />} />
            <Route path="/global-referrals" element={<GlobalReferrals />} />
            <Route path="/add-referral" element={<AddReferral />} />
            <Route path="/add-bulk-referral" element={<AddBulkReferral />} />
            <Route path="/knowledge-based" element={<KnowledgeBased />} />
            <Route path="/view-mentees" element={<ViewMentees />} />
            <Route path="/attempts" element={<Attempts />} />
          </Route>

          {/* Company Login */}
          <Route path="/company-login" element={<CompanyLogin />} />

          {/* Company Dashboard Routes */}
          <Route
            element={
              <Drawer>
                <Outlet />
              </Drawer>
            }
          >
            <Route path="/company-dashboard" element={<CompanyDashboard />} />
            <Route
              path="/company-global-referrals"
              element={<GlobalReferrals />}
            />
            <Route path="/company-users" element={<CompanyUsers />} />
            <Route path="/add-company-user" element={<AddCompanyUsers />} />
            <Route
              path="/company-quiz-report/:userId"
              element={<CompanyQuizReport />}
            />
            <Route
              path="/company-kb-progress"
              element={<CompanyKbProgress />}
            />
            <Route path="/groups" element={<Groups />} />
            <Route
              path="/ViewMenteesbyMentor"
              element={<ViewMenteesbyMentor />}
            />
            <Route path="/add-mentor/:userId" element={<AddMentor />} />
            <Route path="/add-menttes/:groupID" element={<AddMentees />} />
            <Route path="/connection-request" element={<ConnectionRequest />} />
          </Route>

          {/* Admin Dashboard Routes */}
          <Route
            element={
              <Drawer>
                <Outlet />
              </Drawer>
            }
          >
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/company-master" element={<CompanyMaster />} />
            <Route path="/add-company" element={<AddCompany />} />
            <Route path="/give-category" element={<GiveCategory />} />
            <Route path="/add-category" element={<AddCategory />} />
            <Route path="/knowledge-based/subjects" element={<Subjects />} />
            <Route
              path="/subjects/:subjectId/chapters"
              element={<Chapters />}
            />
            <Route
              path="/subjects/:subjectId/chapters/:chapterId/topics"
              element={<Topics />}
            />
            <Route
              path="/subjects/:subjectId/chapters/:chapterId/topics/:topicId/sections"
              element={<Sections />}
            />
          </Route>

          {/* Default Redirects */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
